'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';

import { cn } from '@/lib/styleUtils';
import { PuzzlePrompt } from '@/modules/captcha/components/PuzzleFrame';
import { randomInt } from '@/modules/captcha/shared/puzzle-utils';
import { useTapChallenge } from '@/modules/captcha/hooks/useTapChallenge';
import { shakeAnimation, tileBase, tileCorrect, tileIdle, tileWrong } from './puzzle.style';
import type { PuzzleProps } from './puzzle.types';

/**
 * Lamp colours when lit.
 *
 * Deliberately fixed hexes rather than theme tokens, and this is the one place in the puzzle set
 * where that is correct: a traffic light is red / amber / green, and a tenant whose primary is
 * blue would otherwise get a blue "stop" lamp — at which point the challenge no longer has a
 * right answer. Chrome around them (the housing, the unlit lamps) does follow the theme.
 */
const LIT = ['#E2554A', '#F0B429', '#2FBF71'] as const;
const UNLIT = 'var(--tw-color-gray-dark)';

/** Answer index maps 1:1 to lamp index — red→Stop, amber→Wait, green→Go. */
const ANSWER_KEYS = ['trafficStop', 'trafficWait', 'trafficGo'] as const;

/**
 * Design 2f — tap the action the lit signal calls for.
 *
 * The only puzzle in the set that tests comprehension rather than aim or matching, which is why
 * it earns a slot: a bot that has learned to click the visually-odd tile gets nothing from it.
 */
export const TrafficLight = ({ onSolved }: PuzzleProps) => {
  const t = useTranslations('Captcha');
  const [lit] = useState(() => randomInt(LIT.length));
  const { wrongIndex, solved, pick } = useTapChallenge(lit, onSolved);

  return (
    <>
      <PuzzlePrompt>{t('trafficLightPrompt')}</PuzzlePrompt>
      <div
        className="tw:mx-auto tw:flex tw:w-[312px] tw:items-center tw:gap-base tw:rounded-8 tw:bg-gray-lighter tw:p-small"
        data-cy="captcha-traffic-light"
      >
        <div
          className="tw:flex tw:h-[146px] tw:w-[60px] tw:shrink-0 tw:flex-col tw:items-center tw:justify-center tw:gap-[9px] tw:rounded-8 tw:bg-gray-darker"
          aria-hidden="true"
        >
          {LIT.map((color, index) => (
            <div
              key={color}
              className="tw:h-massive tw:w-massive tw:rounded-full tw:transition-colors"
              style={{ background: index === lit ? color : UNLIT }}
            />
          ))}
        </div>
        <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-micro">
          {ANSWER_KEYS.map((key, index) => {
            const isWrong = index === wrongIndex;
            const isCorrect = solved && index === lit;
            return (
              <button
                key={key}
                type="button"
                onClick={() => pick(index)}
                className={cn(
                  tileBase,
                  'tw:h-[42px] tw:text-14 tw:font-semibold',
                  !isWrong && !isCorrect && cn(tileIdle, 'tw:text-font'),
                  isCorrect && cn(tileCorrect, 'tw:text-success'),
                  isWrong && cn(tileWrong, shakeAnimation, 'tw:text-error')
                )}
                data-cy={`captcha-traffic-action-${index}`}
              >
                {t(key)}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
};

TrafficLight.displayName = 'TrafficLight';
